/**
 * MinistrareIA - SSE Service
 * Server-Sent Events for real-time communication
 * Simpler and more stable than WebSocket
 */

// Event types (must match backend SSEEventTypes)
export const SSE_EVENTS = {
  // Connection events
  CONNECTED: 'CONNECTED',
  HEARTBEAT: 'HEARTBEAT',
  
  // WhatsApp connection events
  CONNECTION_UPDATE: 'CONNECTION_UPDATE',
  QRCODE_UPDATED: 'QRCODE_UPDATED',
  
  // Message events
  NEW_MESSAGE: 'NEW_MESSAGE',
  MESSAGE_STATUS: 'MESSAGE_STATUS',
  MESSAGE_DELETED: 'MESSAGE_DELETED',
  
  // Chat events
  CHAT_UPDATE: 'CHAT_UPDATE',
  CHATS_SYNC: 'CHATS_SYNC',
  
  // Presence events
  PRESENCE_UPDATE: 'PRESENCE_UPDATE',
  
  // Contact events
  CONTACT_UPDATE: 'CONTACT_UPDATE',

  // Sync events (WA Business)
  SYNC_STARTED: 'SYNC_STARTED',
  SYNC_PROGRESS: 'SYNC_PROGRESS',
  SYNC_CHAT_ADDED: 'SYNC_CHAT_ADDED',
  SYNC_COMPLETED: 'SYNC_COMPLETED',
  SYNC_ERROR: 'SYNC_ERROR',

  // System events
  ERROR: 'ERROR',
};

// Connection states
export const SSE_STATES = {
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',
  RECONNECTING: 'reconnecting',
  FAILED: 'failed',
};

class SSEService {
  constructor() {
    this.eventSource = null;
    this.state = SSE_STATES.DISCONNECTED;
    this.listeners = new Map();
    this.token = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    this.reconnectTimeout = null;
    
    // Bind methods
    this.connect = this.connect.bind(this);
    this.disconnect = this.disconnect.bind(this);
  }

  /**
   * Get SSE URL from API base URL
   */
  getSSEUrl(token) {
    const apiUrl = "https://quora-backend-cl3.kinddesert-624c9557.brazilsouth.azurecontainerapps.io";
    
    // Remove trailing slash
    const baseUrl = apiUrl.replace(/\/$/, '');
    
    return `${baseUrl}/events?token=${token}`;
  }

  /**
   * Connect to SSE server
   * @param {string} token - JWT authentication token
   */
  connect(token) {
    if (!token) {
      console.error('[SSE] No token provided');
      return false;
    }

    // Store token for reconnection
    this.token = token;

    // Prevent multiple connections
    if (this.eventSource && this.state === SSE_STATES.CONNECTED) {
      console.log('[SSE] Already connected');
      return true;
    }

    // Close existing connection if any
    this.disconnect(false);

    try {
      this.state = SSE_STATES.CONNECTING;
      this.emit('stateChange', { state: this.state });
      
      const sseUrl = this.getSSEUrl(token);
      console.log('[SSE] Connecting to:', sseUrl.replace(/token=.*/, 'token=***'));
      
      this.eventSource = new EventSource(sseUrl);
      
      // Connection opened
      this.eventSource.onopen = () => {
        console.log('[SSE] Connection opened');
        this.state = SSE_STATES.CONNECTED;
        this.reconnectAttempts = 0;
        this.emit('stateChange', { state: this.state });
      };
      
      // Generic message handler (for events without specific type)
      this.eventSource.onmessage = (event) => {
        this.handleMessage(event);
      };
      
      // Error handler
      this.eventSource.onerror = (error) => {
        this.handleError(error);
      };
      
      // Register specific event listeners
      this.registerEventListeners();
      
      return true;
    } catch (error) {
      console.error('[SSE] Connection error:', error);
      this.state = SSE_STATES.FAILED;
      this.emit('stateChange', { state: this.state, error });
      return false;
    }
  }

  /**
   * Register listeners for specific SSE event types
   */
  registerEventListeners() {
    if (!this.eventSource) return;
    
    // List of events to listen for
    const eventTypes = Object.values(SSE_EVENTS);
    
    eventTypes.forEach(eventType => {
      this.eventSource.addEventListener(eventType, (event) => {
        this.handleTypedMessage(eventType, event);
      });
    });
  }

  /**
   * Handle generic message
   */
  handleMessage(event) {
    try {
      const data = JSON.parse(event.data);
      console.log('[SSE] Generic message:', data);
      this.emit('message', data);
    } catch (error) {
      console.error('[SSE] Error parsing message:', error);
    }
  }

  /**
   * Handle typed message (specific event type)
   */
  handleTypedMessage(eventType, event) {
    try {
      const message = JSON.parse(event.data);
      const { type, data, timestamp } = message;
      
      // Log message (except frequent ones)
      if (type !== SSE_EVENTS.HEARTBEAT && type !== SSE_EVENTS.PRESENCE_UPDATE) {
        console.log('[SSE] Received:', type, data);
      }
      
      // Update state on CONNECTED event
      if (type === SSE_EVENTS.CONNECTED) {
        this.state = SSE_STATES.CONNECTED;
        this.emit('stateChange', { state: this.state });
      }
      
      // Emit event to listeners
      this.emit(type, data, timestamp);
      
      // Also emit generic 'message' event
      this.emit('message', { type, data, timestamp });
      
    } catch (error) {
      console.error('[SSE] Error parsing typed message:', error);
    }
  }

  /**
   * Handle connection error
   */
  handleError(error) {
    console.error('[SSE] Error:', error);
    
    // EventSource automatically reconnects on error
    // But we track state for UI feedback
    if (this.eventSource?.readyState === EventSource.CLOSED) {
      this.state = SSE_STATES.DISCONNECTED;
      this.emit('stateChange', { state: this.state });
      this.emit(SSE_EVENTS.ERROR, { error: 'Connection closed' });
      
      // Manual reconnect if browser doesn't auto-reconnect
      this.scheduleReconnect();
    } else if (this.eventSource?.readyState === EventSource.CONNECTING) {
      this.state = SSE_STATES.RECONNECTING;
      this.reconnectAttempts++;
      this.emit('stateChange', { 
        state: this.state,
        attempt: this.reconnectAttempts,
        maxAttempts: this.maxReconnectAttempts
      });
    }
  }

  /**
   * Schedule reconnection attempt
   */
  scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('[SSE] Max reconnection attempts reached');
      this.state = SSE_STATES.FAILED;
      this.emit('stateChange', { state: this.state });
      return;
    }

    // Exponential backoff: 1s, 2s, 4s, 8s, ... max 30s
    const delay = Math.min(
      1000 * Math.pow(2, this.reconnectAttempts),
      30000
    );

    console.log(`[SSE] Reconnecting in ${delay/1000}s (attempt ${this.reconnectAttempts + 1}/${this.maxReconnectAttempts})`);

    this.reconnectTimeout = setTimeout(() => {
      if (this.token && this.state !== SSE_STATES.CONNECTED) {
        this.connect(this.token);
      }
    }, delay);
  }

  /**
   * Disconnect from SSE server
   * @param {boolean} clearToken - Whether to clear stored token
   */
  disconnect(clearToken = true) {
    console.log('[SSE] Disconnecting...');
    
    // Clear reconnect timeout
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    
    // Close EventSource
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
    
    // Clear token if requested (prevents auto-reconnect)
    if (clearToken) {
      this.token = null;
      this.reconnectAttempts = this.maxReconnectAttempts; // Prevent reconnect
    }
    
    this.state = SSE_STATES.DISCONNECTED;
    this.emit('stateChange', { state: this.state });
  }

  /**
   * Subscribe to an event
   * @param {string} event - Event name
   * @param {function} callback - Callback function
   * @returns {function} Unsubscribe function
   */
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);
    
    // Return unsubscribe function
    return () => this.off(event, callback);
  }

  /**
   * Unsubscribe from an event
   * @param {string} event - Event name
   * @param {function} callback - Callback function
   */
  off(event, callback) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).delete(callback);
    }
  }

  /**
   * Emit an event to all listeners
   * @param {string} event - Event name
   * @param {any} data - Event data
   * @param {string} timestamp - Event timestamp
   */
  emit(event, data, timestamp) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        try {
          callback(data, timestamp);
        } catch (error) {
          console.error(`[SSE] Error in listener for ${event}:`, error);
        }
      });
    }
  }

  /**
   * Check if connected
   */
  isConnected() {
    return this.state === SSE_STATES.CONNECTED && 
           this.eventSource?.readyState === EventSource.OPEN;
  }

  /**
   * Get current state
   */
  getState() {
    return this.state;
  }

  /**
   * Reset reconnection attempts
   */
  resetReconnectAttempts() {
    this.reconnectAttempts = 0;
  }

  /**
   * Force reconnect
   */
  reconnect() {
    this.reconnectAttempts = 0;
    this.disconnect(false);
    if (this.token) {
      this.connect(this.token);
    }
  }
}

// Singleton instance
const sseService = new SSEService();

export default sseService;